import React, { useState } from "react";

// Preset ranges matching GA4
const PRESETS = [
  { label: "Today", getDates: () => {
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10);
    return [dateStr, dateStr];
  }},
  { label: "Yesterday", getDates: () => {
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86400000);
    const dateStr = yesterday.toISOString().slice(0, 10);
    return [dateStr, dateStr];
  }},
  { label: "Last 7 days", getDates: () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 6 * 86400000);
    return [sevenDaysAgo.toISOString().slice(0, 10), today.toISOString().slice(0, 10)];
  }},
  { label: "Last 30 days", getDates: () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 29 * 86400000);
    return [thirtyDaysAgo.toISOString().slice(0, 10), today.toISOString().slice(0, 10)];
  }},
  { label: "This month", getDates: () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return [firstDay.toISOString().slice(0, 10), today.toISOString().slice(0, 10)];
  }},
  { label: "Last month", getDates: () => {
    const today = new Date();
    const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayLastMonth = new Date(firstDayThisMonth.getTime() - 86400000);
    const firstDayLastMonth = new Date(lastDayLastMonth.getFullYear(), lastDayLastMonth.getMonth(), 1);
    return [firstDayLastMonth.toISOString().slice(0, 10), lastDayLastMonth.toISOString().slice(0, 10)];
  }},
  { label: "Last 90 days", getDates: () => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today.getTime() - 89 * 86400000);
    return [ninetyDaysAgo.toISOString().slice(0, 10), today.toISOString().slice(0, 10)];
  }},
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

// ─── FIX 2: Added onPrev / onNext props for month navigation arrows ───────────
function CalendarMonth({ year, month, selectedStart, selectedEnd, onDayClick, onPrev, onNext }) {
  const monthName = new Date(year, month, 1).toLocaleString("default", { month: "long", year: "numeric" });
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const isSelected = dateStr === selectedStart || dateStr === selectedEnd;
    const isInRange =
      selectedStart && selectedEnd && dateStr > selectedStart && dateStr < selectedEnd;

    days.push({ day, dateStr, isSelected, isInRange });
  }

  return (
    <div className="gsc-cal-inn">
      {/* Month header with prev / next arrows */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        {/* FIX 2: Prev arrow — only shown on left calendar */}
        <button
          onClick={onPrev}
          disabled={!onPrev}
          style={{
            visibility: onPrev ? "visible" : "hidden",
            background: "transparent",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            width: 28,
            height: 28,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: "#374151",
            padding: 0,
          }}
          aria-label="Previous month"
        >
          ‹
        </button>

        <div className="gsc-cal-title" style={{ margin: 0 }}>{monthName}</div>

        {/* FIX 2: Next arrow — only shown on right calendar */}
        <button
          onClick={onNext}
          disabled={!onNext}
          style={{
            visibility: onNext ? "visible" : "hidden",
            background: "transparent",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            width: 28,
            height: 28,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: "#374151",
            padding: 0,
          }}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="gsc-cal-days">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="gsc-cal-day">{d}</div>
        ))}
        {days.map((item, idx) => (
          <div
            key={idx}
            onClick={() => item && onDayClick(item.dateStr)}
            className="gsc-cal-date"
            style={{
              cursor: item ? "pointer" : "default",
              backgroundColor:
                item?.isSelected ? "#1f2937" :
                item?.isInRange  ? "#e5f3ff" :
                "transparent",
              color: item?.isSelected ? "white" : "#1f2937",
              fontWeight: item?.isSelected ? 600 : 400,
              border: item?.isSelected ? "2px solid #2563eb" : "none",
            }}
          >
            {item?.day || ""}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FIX 1: Added `onClose` prop so the parent can close the picker ───────────
export default function CalendarPicker({ startDate, endDate, onDateRangeSelect, onClose, isComparing = false }) {
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [selectingPhase, setSelectingPhase] = useState("start");

  // FIX 2: viewOffset lets the user navigate to previous / future months
  const [viewOffset, setViewOffset] = useState(0);

  const today = new Date();
  const baseDate = new Date(today.getFullYear(), today.getMonth() + viewOffset, 1);
  const leftYear  = baseDate.getFullYear();
  const leftMonth = baseDate.getMonth();

  const rightDate  = new Date(leftYear, leftMonth + 1, 1);
  const rightYear  = rightDate.getFullYear();
  const rightMonth = rightDate.getMonth();

  const handleDayClick = (dateStr) => {
    if (selectingPhase === "start") {
      setTempStart(dateStr);
      setTempEnd(dateStr);
      setSelectingPhase("end");
    } else {
      if (dateStr >= tempStart) {
        setTempEnd(dateStr);
      } else {
        setTempStart(dateStr);
        setTempEnd(tempStart);
      }
      setSelectingPhase("start");
    }
  };

  const handlePreset = (preset) => {
    const [start, end] = preset.getDates();
    setTempStart(start);
    setTempEnd(end);
    onDateRangeSelect(start, end);
    // FIX 1: close after preset selection
    if (onClose) onClose();
  };

  const handleApply = () => {
    onDateRangeSelect(tempStart, tempEnd);
    // FIX 1: close after apply
    if (onClose) onClose();
  };

  // FIX 1: Reset temp values and close the picker on Cancel
  const handleCancel = () => {
    setTempStart(startDate);
    setTempEnd(endDate);
    if (onClose) onClose();
  };

  return (
    <div className="gsc-popup">
      {/* Preset ranges sidebar */}
      <div className="gsc-pop-inn">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => handlePreset(preset)}
            className="gsc-pop-btn"
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f3f4f6")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="gsc-cal-grid">
        {/* FIX 2: Pass onPrev to left calendar, onNext to right calendar */}
        <CalendarMonth
          year={leftYear}
          month={leftMonth}
          selectedStart={tempStart}
          selectedEnd={tempEnd}
          onDayClick={handleDayClick}
          isComparing={isComparing}
          onPrev={() => setViewOffset((v) => v - 1)}
          onNext={null}
        />
        <CalendarMonth
          year={rightYear}
          month={rightMonth}
          selectedStart={tempStart}
          selectedEnd={tempEnd}
          onDayClick={handleDayClick}
          isComparing={isComparing}
          onPrev={null}
          onNext={() => setViewOffset((v) => v + 1)}
        />
      </div>

      {/* Date inputs and action buttons */}
      <div className="gsc-dates">
        <div>
          <label className="gsc-date-label">Start date</label>
          <input
            type="date"
            value={tempStart}
            onChange={(e) => setTempStart(e.target.value)}
            className="gsc-date-input"
          />
        </div>
        <div>
          <label className="gsc-date-label">End date</label>
          <input
            type="date"
            value={tempEnd}
            onChange={(e) => setTempEnd(e.target.value)}
            className="gsc-date-input"
          />
        </div>
        <div className="gsc-btns-wrap">
          <button
            onClick={handleCancel}
            className="gsc-btns-cancel"
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f9fafb")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="gsc-btns-apply"
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#2563eb")}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}